import { useState, useEffect } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";
import ParcoursView from "./ParcoursView";
import OdysboardView from "../pages/shared/OdysboardView";
import CoachingSessionsView from "../pages/shared/CoachingSessionsView";
import CoachInsightsPanel from "./CoachInsightsPanel";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-screen admin dossier of a student.
 * Tabs: Aperçu | Parcours | Odysboard | Sessions Fathom | Visites | Espace Coach (read-only) | Activité
 * Export PDF button on top right.
 */
export default function AdminStudentDossier({ student, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [activity, setActivity] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    if (!student?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [studentRes, visitsRes, activityRes] = await Promise.all([
          api.get(`/parcours/student/${student.id}`).catch(() => ({ data: null })),
          api.get(`/visits/${student.id}`).catch(() => ({ data: [] })),
          api.get(`/activity-logs/${student.id}`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setOverview(studentRes.data);
        setVisits(Array.isArray(visitsRes.data) ? visitsRes.data : []);
        setActivity(Array.isArray(activityRes.data) ? activityRes.data : []);
      } catch (e) {
        if (!cancelled) toast({ variant: "destructive", title: "Erreur de chargement" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [student?.id, toast]);

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${REACT_APP_BACKEND_URL}/api/export/pdf/${student.id}?token=${encodeURIComponent(token || "")}`;
      window.open(url, "_blank");
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur PDF" });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
    } catch { return iso; }
  };

  const tabs = [
    { key: "overview", label: "Aperçu" },
    { key: "parcours", label: "Parcours" },
    { key: "odysboard", label: "Odysboard" },
    { key: "sessions", label: "Sessions Fathom" },
    { key: "visits", label: "Visites" },
    { key: "coach-space", label: "Espace coach" },
    { key: "activity", label: "Activité" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch"
      style={{ background: "rgba(8, 13, 26, 0.65)", backdropFilter: "blur(4px)" }}
      data-testid="admin-student-dossier"
    >
      <div className="relative w-full max-w-7xl mx-auto my-4 sm:my-8 flex flex-col rounded-2xl overflow-hidden" style={{ background: "var(--paper)" }}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-line flex items-center justify-between flex-wrap gap-3" style={{ background: "linear-gradient(135deg, var(--ink) 0%, var(--ink-dark) 100%)" }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--gold), var(--gold-dark))" }}>
              <span className="text-white text-base font-bold" style={{ fontFamily: "var(--serif)" }}>
                {student?.first_name?.[0]}{student?.last_name?.[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "var(--serif)" }}>
                {student?.first_name} {student?.last_name}
              </h2>
              <p className="text-xs italic" style={{ color: "rgba(233, 220, 196, 0.85)", fontFamily: "var(--serif)" }}>
                {student?.email} · Dossier complet (vue admin)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="dossier-export-pdf-btn"
              onClick={handleExportPDF}
              className="btn-gold text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF
            </button>
            <button
              data-testid="dossier-close-btn"
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: "#FFFFFF" }}
              onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
              onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 py-3 border-b border-line bg-paper-2 flex gap-2 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.key}
              data-testid={`dossier-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`tab-button whitespace-nowrap ${activeTab === t.key ? "tab-button-active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <p className="text-center text-sm italic" style={{ color: "var(--muted)" }}>Chargement…</p>
          )}

          {!loading && activeTab === "overview" && (
            <div className="space-y-6" data-testid="dossier-overview">
              {/* KPI row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glass-card rounded-2xl p-4">
                  <p className="metric-number" style={{ fontSize: "1.8rem" }}>
                    {overview?.progress?.main_progress ?? 0}%
                  </p>
                  <p className="metric-label mt-1">Progression</p>
                </div>
                <div className="glass-card rounded-2xl p-4">
                  <p className="metric-number" style={{ fontSize: "1.8rem", color: "var(--gold-dark)" }}>
                    {overview?.points ?? 0}
                  </p>
                  <p className="metric-label mt-1">Points</p>
                </div>
                <div className="glass-card rounded-2xl p-4">
                  <p className="metric-number" style={{ fontSize: "1.8rem", color: "var(--teal)" }}>
                    {visits.length}
                  </p>
                  <p className="metric-label mt-1">Visites</p>
                </div>
                <div className="glass-card rounded-2xl p-4">
                  <p className="metric-number" style={{ fontSize: "1.8rem" }}>
                    {activity.length}
                  </p>
                  <p className="metric-label mt-1">Événements</p>
                </div>
              </div>

              {/* Identity card */}
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-base font-bold mb-3" style={{ fontFamily: "var(--serif)" }}>Identité</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)", letterSpacing: "0.08em" }}>Email</span><p>{student?.email}</p></div>
                  <div><span className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)", letterSpacing: "0.08em" }}>Statut</span><p>{student?.is_active ? "Actif" : "Inactif"}</p></div>
                  <div><span className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)", letterSpacing: "0.08em" }}>Inscrit le</span><p>{formatDate(student?.created_at)}</p></div>
                  <div><span className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)", letterSpacing: "0.08em" }}>Dernière connexion</span><p>{formatDate(student?.last_login)}</p></div>
                </div>
              </div>

              {/* Recent activity */}
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-base font-bold mb-3" style={{ fontFamily: "var(--serif)" }}>Activité récente</h3>
                {activity.length === 0 ? (
                  <p className="text-sm italic" style={{ color: "var(--muted)" }}>Aucune activité enregistrée.</p>
                ) : (
                  <ul className="space-y-2">
                    {activity.slice(0, 5).map((a, i) => (
                      <li key={a.id || i} className="text-sm flex justify-between border-b border-line py-2 last:border-0">
                        <span>{a.action || "—"}</span>
                        <span className="text-xs italic" style={{ color: "var(--muted)" }}>{formatDate(a.timestamp)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === "parcours" && (
            <ParcoursView studentId={student.id} isCoach={true} />
          )}

          {!loading && activeTab === "odysboard" && (
            <OdysboardView studentIdOverride={student.id} />
          )}

          {!loading && activeTab === "sessions" && (
            <CoachingSessionsView studentId={student.id} isCoachView={true} />
          )}

          {!loading && activeTab === "visits" && (
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-base font-bold mb-4" style={{ fontFamily: "var(--serif)" }}>Visites ({visits.length})</h3>
              {visits.length === 0 ? (
                <p className="text-sm italic" style={{ color: "var(--muted)" }}>Aucune visite enregistrée.</p>
              ) : (
                <ul className="space-y-3">
                  {visits.map((v, i) => (
                    <li key={v.id || i} className="border-b border-line py-3 last:border-0">
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <p className="font-medium text-sm">{v.address || v.title || "Sans adresse"}</p>
                          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>{formatDate(v.visit_date || v.created_at)}</p>
                        </div>
                        {v.rating && (
                          <span className="badge-validated text-xs">{v.rating}/5</span>
                        )}
                      </div>
                      {v.notes && <p className="text-xs italic mt-2" style={{ color: "var(--ink-soft)", fontFamily: "var(--serif)" }}>« {v.notes} »</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!loading && activeTab === "coach-space" && (
            <CoachInsightsPanel studentId={student.id} studentName={`${student.first_name} ${student.last_name}`} isReadOnly={true} />
          )}

          {!loading && activeTab === "activity" && (
            <div className="glass-card rounded-2xl p-6" data-testid="dossier-activity">
              <h3 className="text-base font-bold mb-4" style={{ fontFamily: "var(--serif)" }}>Journal d'activité ({activity.length})</h3>
              {activity.length === 0 ? (
                <p className="text-sm italic" style={{ color: "var(--muted)" }}>Aucune activité enregistrée.</p>
              ) : (
                <ul className="space-y-2">
                  {activity.map((a, i) => (
                    <li key={a.id || i} className="flex justify-between border-b border-line py-2 last:border-0 text-sm">
                      <div>
                        <p className="font-medium">{a.action || "—"}</p>
                        {a.actor_name && <p className="text-xs" style={{ color: "var(--muted)" }}>par {a.actor_name} ({a.actor_role || "?"})</p>}
                      </div>
                      <span className="text-xs italic" style={{ color: "var(--muted)" }}>{formatDate(a.timestamp)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
